import { useQuery } from '@tanstack/react-query';
import {
  decodeXlsxPresentationBase64Content,
  parseXlsxPresentationOverrides,
  type XlsxPresentationOverrides,
} from 'components/SpreadsheetNotes/utilities/xlsxPresentationOverrides.utils';

export const XLSX_PREVIEW_CONTENT_STALE_TIME_IN_MS = 1000 * 60 * 60 * 24;
const XLSX_PRESENTATION_OVERRIDES_QUERY_VERSION = 2;

interface UseXlsxPresentationOverridesParams {
  base64Content?: string;
  fileId: number;
  isSourceLoading?: boolean;
  sourceUrl?: string;
}

export const useXlsxPresentationOverrides = ({
  base64Content,
  fileId,
  isSourceLoading = false,
  sourceUrl,
}: UseXlsxPresentationOverridesParams) => {
  const presentationSourceKey = base64Content ? 'base64-content' : sourceUrl;

  const { data, isLoading } = useQuery<XlsxPresentationOverrides | undefined>({
    enabled: Boolean(base64Content || sourceUrl),
    queryFn: async () => {
      if (base64Content) {
        return parseXlsxPresentationOverrides(decodeXlsxPresentationBase64Content(base64Content));
      }

      if (!sourceUrl) {
        return undefined;
      }

      const response = await fetch(sourceUrl);
      if (!response.ok) {
        throw new Error(`Unable to load XLSX source for presentation overrides: ${response.status}`);
      }

      return parseXlsxPresentationOverrides(await response.arrayBuffer());
    },
    queryKey: [
      'spreadsheet-presentation-overrides',
      XLSX_PRESENTATION_OVERRIDES_QUERY_VERSION,
      fileId,
      presentationSourceKey,
    ],
    retry: false,
    staleTime: XLSX_PREVIEW_CONTENT_STALE_TIME_IN_MS,
  });

  return {
    isLoading: isSourceLoading || isLoading,
    presentationOverrides: data,
  };
};
